import { Component, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { ApiService } from '../../core/service/api.service';
import { API_ENDPOINTS } from '../../core/api/api-endpoint';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateModule } from '@ngx-translate/core';

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const newPassword = control.get('newPassword')?.value;
  const confirmPassword = control.get('confirmPassword')?.value;
  return newPassword && confirmPassword && newPassword !== confirmPassword
    ? { passwordMismatch: true }
    : null;
}

// Min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#])[A-Za-z\d@$!%*?&^#]{8,}$/;

@Component({
  selector: 'app-change-password',
  imports: [ReactiveFormsModule, TranslatePipe, TranslateModule],
  templateUrl: './change-password.html',
  styleUrl: './change-password.scss',
})
export class ChangePassword {

  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  private toastr = inject(ToastrService);

  isSubmitting = false;

  showCurrent = false;
  showNew = false;
  showConfirm = false;

  form: FormGroup = this.fb.group(
    {
      currentPassword: ['', [Validators.required, Validators.maxLength(15)]],
      newPassword: ['', [Validators.required, Validators.maxLength(15), Validators.pattern(PASSWORD_PATTERN)]],
      confirmPassword: ['', [Validators.required, Validators.maxLength(15)]],
    },
    { validators: passwordMatchValidator }
  );

  get currentPassword() { return this.form.get('currentPassword')!; }
  get newPassword() { return this.form.get('newPassword')!; }
  get confirmPassword() { return this.form.get('confirmPassword')!; }

  isInvalid(control: AbstractControl): boolean {
    return control.invalid && (control.dirty || control.touched);
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const { currentPassword, newPassword, confirmPassword } = this.form.value;
    this.isSubmitting = true;

    this.api.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, { currentPassword, newPassword, confirmPassword }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.activeModal.close();
      },
      error: (err: any) => {
        this.isSubmitting = false;
        const msg = err?.error?.message;
        this.toastr.error(msg);
      }
    });
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }
}
